import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, CircularProgress, Alert, Chip, InputBase,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import {
    RemoveCircleOutline, AddCircleOutline, Close, Search as SearchIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { productCategoryList } from '../utils/groupByCategory';
import { TOUCH } from '../theme/tokens';
import { formatINR } from './common/Money';
import ScrollChipRow from './common/ScrollChipRow';

const NO_CATEGORY = 'No category';
const rupees = (n) => formatINR(n, { decimals: Number.isInteger(Math.round((Number(n) || 0) * 100) / 100) ? 0 : 2 });
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatError = (err) => {
    if (Array.isArray(err.response?.data?.detail))
        return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    return err.response?.data?.detail || err.message || 'Something went wrong';
};

/**
 * Add plans for one customer, laid out like the order page: one start date at the top, then
 * every product a plan can be made for, by category, each with Add → − [qty] +. Several plans
 * are added in one go. A drop-down of 20–30 products was hard to pick from on a phone.
 *
 * Used by CustomerPlans, so it is the same on the Plans page and the Customers tab.
 *
 * products   all business products (plans included)
 * takenIds   product ids the customer already has a current plan for — shown, but not addable
 * onAdded(affectedClosedMonths) after saving
 */
export default function AddPlansDialog({ open, businessId, customer, products, takenIds, onClose, onAdded }) {
    const [startDate, setStartDate] = useState('');
    const [qty, setQty] = useState({});          // productId -> number chosen
    const [draft, setDraft] = useState({});      // productId -> text while typing
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [categoryOrder, setCategoryOrder] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setStartDate(toISODate(new Date()));
        setQty({}); setDraft({}); setSearch(''); setCatFilter(''); setError(''); setSaving(false);
        businessAPI.getCategoryOrder(businessId)
            .then(res => setCategoryOrder(res.data?.ordered_names || []))
            .catch(() => setCategoryOrder([]));
    }, [open, businessId]);

    // Every live product, as the old drop-down offered. Not only those marked "plans": vendors do
    // put customers on plans for products marked orders-only (older products predate the setting),
    // and hiding those would quietly take away products they use.
    const groups = useMemo(() => {
        const list = (products || [])
            .filter(p => !p.f_is_archived)
            .sort((a, b) => (a.f_sort_order || 0) - (b.f_sort_order || 0) || (a.f_name || '').localeCompare(b.f_name || ''));
        const byCat = {};
        const seen = [];
        for (const p of list) {
            const c = productCategoryList(p)[0] || NO_CATEGORY;
            if (!byCat[c]) { byCat[c] = []; seen.push(c); }
            byCat[c].push(p);
        }
        const order = [
            ...categoryOrder.filter(c => byCat[c] && c !== NO_CATEGORY),
            ...seen.filter(c => c !== NO_CATEGORY && !categoryOrder.includes(c)),
            ...(byCat[NO_CATEGORY] ? [NO_CATEGORY] : []),
        ];
        return order.map(c => ({ cat: c, items: byCat[c] }));
    }, [products, categoryOrder]);

    const q = search.trim().toLowerCase();
    const shown = groups
        .filter(g => !catFilter || g.cat === catFilter)
        .map(g => ({ ...g, items: g.items.filter(p => !q || (p.f_name || '').toLowerCase().includes(q)) }))
        .filter(g => g.items.length);

    const chosen = Object.entries(qty).filter(([, n]) => n > 0);
    const setTo = (id, n) => setQty(prev => {
        const c = { ...prev };
        const v = Math.max(0, Math.round(n * 1000) / 1000);
        if (v > 0) c[id] = v; else delete c[id];
        return c;
    });
    const typeQty = (id, text) => {
        if (!/^\d*\.?\d{0,3}$/.test(text)) return;
        setDraft(d => ({ ...d, [id]: text }));
        const n = parseFloat(text);
        setTo(id, Number.isFinite(n) ? n : 0);
    };
    const endTyping = (id) => setDraft(d => { const c = { ...d }; delete c[id]; return c; });

    const handleSave = async () => {
        if (!chosen.length) return;
        if (!startDate) { setError('Pick the date the plans start from.'); return; }
        setSaving(true);
        setError('');
        const failed = [];
        let affected = [];
        for (const [pid, n] of chosen) {
            try {
                const res = await businessAPI.createCustomerSubscription({
                    f_business_id: businessId,
                    f_business_customer_id: customer.f_id,
                    f_business_product_id: Number(pid),
                    f_quantity: n,
                    f_effective_from_date: startDate,
                    f_updated_by: parseInt(localStorage.getItem('user_id')),
                    f_action: 'Start',
                    f_reason: null,
                });
                affected = affected.concat(res.data.affected_locked_periods || []);
                setQty(prev => { const c = { ...prev }; delete c[pid]; return c; });   // done — off the list
            } catch (err) {
                const name = products.find(p => p.f_id === Number(pid))?.f_name || 'A product';
                failed.push(`${name}: ${formatError(err)}`);
            }
        }
        setSaving(false);
        const seen = new Set();
        const uniq = affected.filter(a => !seen.has(a.period_display) && seen.add(a.period_display));
        if (failed.length) {
            setError(failed.join(' · '));
            onAdded && onAdded(uniq, true);   // some were added — refresh, keep the dialog open
        } else {
            onAdded && onAdded(uniq, false);
        }
    };

    const stepper = (id) => {
        const v = qty[id] || 0;
        if (v === 0 && draft[id] === undefined) {
            return (
                <Button variant="outlined" size="small" disabled={saving} onClick={() => setTo(id, 1)}
                    sx={{ minWidth: 64, height: TOUCH.dense, fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}>
                    Add
                </Button>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton aria-label="Less" disabled={saving} onClick={() => { endTyping(id); setTo(id, v - 1); }}
                    sx={{ width: TOUCH.min, height: TOUCH.min }}>
                    <RemoveCircleOutline color="primary" />
                </IconButton>
                <InputBase value={draft[id] ?? String(v)} onChange={e => typeQty(id, e.target.value)}
                    onFocus={e => e.target.select()} onBlur={() => endTyping(id)} disabled={saving}
                    inputProps={{ inputMode: 'decimal', 'aria-label': 'Items per day', style: { textAlign: 'center', fontWeight: 700, padding: '6px 0' } }}
                    sx={{ width: 52, height: 36, fontSize: '1rem', border: 1, borderColor: 'divider', borderRadius: 1,
                          '&.Mui-focused': { borderColor: 'primary.main' } }} />
                <IconButton aria-label="More" disabled={saving} onClick={() => { endTyping(id); setTo(id, v + 1); }}
                    sx={{ width: TOUCH.min, height: TOUCH.min }}>
                    <AddCircleOutline color="primary" />
                </IconButton>
            </Box>
        );
    };

    const headingSx = {
        display: 'block', mt: 2, mb: 0.25, px: 1, py: 0.5,
        fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5,
        bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
        borderLeft: 3, borderColor: 'primary.main', borderRadius: 0.5,
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>Add plans</Typography>
                    <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.9 }}>{customer?.f_name}</Typography>
                </Box>
                <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ color: 'inherit' }}><Close /></IconButton>
            </DialogTitle>

            {/* Start date, search and categories stay put while the list scrolls */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                <TextField type="date" size="small" fullWidth label="Plans start from" value={startDate}
                    onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1 }} />
                <TextField size="small" fullWidth placeholder="Search item" value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear"><ClearIcon fontSize="small" /></IconButton>
                            </InputAdornment>
                        ) : null,
                    }} />
                {groups.length > 1 && (
                    <ScrollChipRow px={2} sx={{ mt: 1, mx: -2 }}>
                        <Chip label="All" size="small" clickable sx={{ flexShrink: 0 }}
                            color={!catFilter ? 'primary' : 'default'} variant={!catFilter ? 'filled' : 'outlined'}
                            onClick={() => setCatFilter('')} />
                        {groups.map(g => (
                            <Chip key={g.cat} label={`${g.cat} (${g.items.length})`} size="small" clickable sx={{ flexShrink: 0 }}
                                color={catFilter === g.cat ? 'primary' : 'default'}
                                variant={catFilter === g.cat ? 'filled' : 'outlined'}
                                onClick={() => setCatFilter(catFilter === g.cat ? '' : g.cat)} />
                        ))}
                    </ScrollChipRow>
                )}
            </Box>

            <DialogContent sx={{ px: 2, pt: 0 }}>
                {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
                {shown.map(g => (
                    <React.Fragment key={g.cat}>
                        <Typography variant="subtitle2" sx={headingSx}>{g.cat}</Typography>
                        {g.items.map(p => {
                            const taken = takenIds.has(p.f_id);
                            const flat = (p.f_billing_type || p.product_billing_type) === 'MonthlyFlatFee';
                            const days = p.schedule_label && p.schedule_label !== 'Every day' ? p.schedule_label : null;
                            const price = p.current_price != null
                                ? `${rupees(p.current_price)}${flat ? ' a month' : ' each'}` : null;
                            return (
                                <Box key={p.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: TOUCH.min,
                                                        py: 0.25, pl: 1.5, borderBottom: 1, borderColor: 'divider',
                                                        opacity: taken ? 0.6 : 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{p.f_name}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                            {[price, days].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                    {taken ? (
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ flexShrink: 0 }}>
                                            Already on a plan
                                        </Typography>
                                    ) : stepper(p.f_id)}
                                </Box>
                            );
                        })}
                    </React.Fragment>
                ))}
                {shown.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        {q ? 'No item matches your search.' : 'No products yet. Add them in Settings → Products.'}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                    {chosen.length ? `${chosen.length} plan${chosen.length !== 1 ? 's' : ''} selected` : ''}
                </Typography>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || !chosen.length}
                    sx={{ minWidth: 120, height: TOUCH.min }}>
                    {saving ? <CircularProgress size={20} color="inherit" />
                        : chosen.length > 1 ? `Add ${chosen.length} plans` : 'Add plan'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
